/**
 * GameLogicService - Service de logique de jeu
 * Identique à Flutter GameLogicService
 */

import {PlayerStep} from '../models/PlayerStep';
import {GameState, GameStateStatus} from '../models/GameState';
import {PlayerPath} from '../models/PlayerPath';
import {PlayerAction} from '../models/PlayerAction';
import {PlayerNotification} from '../models/PlayerNotification';
import {PlayerAdventure} from '../models/PlayerAdventure';
import {GameDataAttribute} from '../models/GameDataAttribute';
import {NotebookEntry} from '../models/NotebookEntry';
import playerService from './playerService';
import gameStateService from './gameStateService';
import conditionHelper from '../helpers/ConditionHelper';
import GameRegistry from '../systems/GameRegistry';
import {SimpleSystem} from '../systems/simple/SimpleSystem';
import GameStateHelper from '../helpers/GameStateHelper';

export class GameLogicService {
  private currentGameState: GameState | null = null;
  private currentAdventure: PlayerAdventure | null = null;

  /**
   * Joue une partie
   */
  async playGame(adventureURI: string, slot: number): Promise<void> {
    const gameState = await playerService.getGameState(adventureURI, slot);
    if (!gameState) {
      throw new Error('Game state not found');
    }
    this.currentGameState = gameState;
  }

  /**
   * Reconstruit un PlayerStep complet depuis un GameState et une Step
   * Reproduit exactement la logique Java GameService.getGameStep()
   * Identique à Flutter GameLogicService.getGameStep
   */
  async getGameStep(
    gameState: GameState,
    currentStep: PlayerStep,
    action: string,
    options: {
      doEventsAndUpdateRecordedPaths?: boolean;
    } = {},
  ): Promise<PlayerStep> {
    const {doEventsAndUpdateRecordedPaths = true} = options;

    if (!this.currentAdventure) {
      throw new Error('No current adventure');
    }

    // Migrer les données notebook si nécessaire (au chargement)
    GameLogicService.migrateNotebookData(gameState);

    // 1. Créer le PlayerStep avec le gameState attaché
    let gameStep: PlayerStep = {
      ...currentStep,
      gameState,
    };

    // 2. Mettre à jour lastAction
    gameState.data.lastAction = action;

    // 3. Appeler gameSystemType.updateGameState()
      const gameSystem = GameRegistry.getSystem(this.currentAdventure.type || 'SIMPLE') as SimpleSystem;
    if (gameSystem) {
      const updatedGameState = gameSystem.updateGameState(gameState, currentStep);
      // Préserver userId lors de l'assignation
      Object.assign(gameState, {
        ...updatedGameState,
        userId: gameState.userId, // Toujours préserver le userId original
      });
    }

    // 4. Traiter les événements de l'étape (AVANT updateGameState, comme en Java)
    // Les événements peuvent modifier les extraFields (ex: D6_PV modifié par un événement)
    const eventNotifications: PlayerNotification[] = [];
    if (doEventsAndUpdateRecordedPaths && currentStep.events) {
      const events = await this.processStepEvents(gameState, currentStep);
      eventNotifications.push(...events);
    }

    // 4b. Traiter le notebook de l'étape (indépendant des événements)
    // Le notebook génère sa propre notification
    console.log(`[GameLogicService] getGameStep: About to process notebook for step ${currentStep.numero}`);
    console.log(`[GameLogicService] getGameStep: doEventsAndUpdateRecordedPaths=${doEventsAndUpdateRecordedPaths}`);
    console.log(`[GameLogicService] getGameStep: currentStep.notebook=`, currentStep.notebook);
    console.log(`[GameLogicService] getGameStep: gameState.data.notebookAuto before:`, JSON.stringify(gameState.data.notebookAuto));
    
    const notebookNotification: PlayerNotification | null = await this.processStepNotebook(gameState, currentStep);
    
    console.log(`[GameLogicService] getGameStep: notebookNotification=`, notebookNotification);
    console.log(`[GameLogicService] getGameStep: gameState.data.notebookAuto after:`, JSON.stringify(gameState.data.notebookAuto));
    
    if (notebookNotification) {
      eventNotifications.push(notebookNotification);
      console.log(`[GameLogicService] getGameStep: Added notebook notification to eventNotifications`);
    }

    // 5. Mettre à jour recorded paths si nécessaire
    if (doEventsAndUpdateRecordedPaths) {
      await this.updateRecordedPaths(gameState, currentStep.numero);
    }

    // 6. Sauvegarder l'avatar (layerAdventurer) dans gameState.data.layerAdventurerId
    // Si la step a un layerAdventurer avec une image, la sauvegarder
    if (currentStep.layerAdventurer) {
      const layerAdventurer = currentStep.layerAdventurer;
      // Si layerAdventurer a un id, l'utiliser (comme en Java)
      if (layerAdventurer['id']) {
        gameState.data.layerAdventurerId = layerAdventurer['id'].toString();
        console.log(`[GameLogicService] Saved layerAdventurer ID to gameState.data.layerAdventurerId: ${layerAdventurer['id']}`);
      } else if (layerAdventurer['type'] === 'picture' && layerAdventurer['image']) {
        // Sinon, si c'est une image base64, la sauvegarder directement
        const avatarImage = layerAdventurer['image'];
        gameState.data.layerAdventurerId = avatarImage;
        console.log('[GameLogicService] Saved layerAdventurer image (base64) to gameState.data.layerAdventurerId');
      }
    }

    // 7. Traiter les attributs de l'étape (non-objects) → ajoutés au gameState
    // APRÈS updateGameState, comme en Java
    const attributeResult = await this.processStepAttributes(gameState, currentStep);
    const attributeNotifications = attributeResult.notifications;
    let gameStateWithAttributes = attributeResult.updatedGameState;

    // Utiliser gameStateWithAttributes pour les opérations suivantes
    gameState = gameStateWithAttributes;

    // 8. Filtrer les paths disponibles
    // IMPORTANT: Stocker les paths originaux avant de filtrer pour le mode éditeur
    const originalPaths = currentStep.paths || [];
    const availablePaths = conditionHelper.filterPaths(currentStep, gameState, false);
    gameStep = {
      ...gameStep,
      paths: availablePaths,
      // Stocker les paths originaux pour le mode éditeur dans BaseStepWidget
      allPaths: originalPaths,
    };

    // 9. Filtrer les actions disponibles
    const availableActions = conditionHelper.filterActions(currentStep, gameState);
    gameStep = {
      ...gameStep,
      actions: availableActions,
    };

    // 10. Initialiser les step extra fields (getOrCreate : ne pas écraser si on repasse sur la step)
    GameStateHelper.initStepExtraFields(
      gameState,
      currentStep.numero,
      currentStep.extraFields,
      currentStep.type,
    );

    // 11. Sauvegarder le gameState (avec layerAdventurerId mis à jour)
    await playerService.updateGameState(gameState);

    // 12. Retourner l'étape avec le gameState attaché et les notifications
    return {
      ...gameStep,
      gameState,
      notifications: [...currentStep.notifications, ...eventNotifications, ...attributeNotifications],
    };
  }

  /**
   * Met à jour les recorded paths (équivalent Java updateRecordedPaths)
   */
  private async updateRecordedPaths(gameState: GameState, numero: number): Promise<void> {
    const recordedPaths = gameState.data.recordedPaths || '';
    const paths = recordedPaths ? recordedPaths.split('-') : [];

    // Vérifier si le step est déjà présent
    const isPresent = paths.includes(numero.toString());

    // Mettre à jour nbDifferentSteps si nouveau step
    if (!isPresent) {
      gameState.data.nbDifferentSteps = (gameState.data.nbDifferentSteps || 0) + 1;
    }

    // Mettre à jour recordedPaths si le dernier step est différent
    const lastOne = paths.length > 0 ? paths[paths.length - 1] : null;
    if (lastOne !== numero.toString()) {
      const newRecordedPaths = recordedPaths ? `${recordedPaths}-${numero}` : numero.toString();
      gameState.data.recordedPaths = newRecordedPaths;
    }
  }

  /**
   * Traite les attributs d'une étape et les ajoute au gameState
   * Suit la logique Java : addNonObjectsToGameState()
   * - OBJECT : ignorés ici (gérés dans getObjects)
   * - FEATURE : ajoutés à gameState.data.skills
   * - BUTTERFLY_EFFECT : ajoutés à gameState.data.butterflyEffects
   * Identique à Flutter PlayerController._processStepAttributes
   */
  private async processStepAttributes(
    gameState: GameState,
    step: PlayerStep,
  ): Promise<{notifications: PlayerNotification[]; updatedGameState: GameState}> {
    const notifications: PlayerNotification[] = [];

    console.log(`[GameLogicService] ===== processStepAttributes START =====`);
    console.log(`[GameLogicService] Step numero: ${step.numero}`);
    console.log(`[GameLogicService] Step attributes count: ${step.attributes?.length || 0}`);
    if (step.attributes && step.attributes.length > 0) {
      console.log(`[GameLogicService] Step attributes:`, step.attributes.map((a: any) => ({
        name: a.name,
        qualification: a.qualification,
        value: a.value,
      })));
    }

    if (step.attributes.length === 0) {
      console.log(`[GameLogicService] No attributes in step`);
      return {notifications, updatedGameState: gameState};
    }

    // Commencer avec le gameState actuel
    let currentGameState = gameState;

    // Parcourir tous les attributs de l'étape
    for (const stepAttr of step.attributes) {
      const attributeId = stepAttr.name;
      const attributeValue = stepAttr.value || '';
      const qualification = stepAttr.qualification?.toUpperCase() || '';

      console.log(
        `[GameLogicService] Processing attribute ${attributeId} (qualification: ${qualification}) = ${attributeValue}`,
      );

      // OBJECT : Ne pas traiter ici (gérés dans getObjects, ajoutés via action pick_up)
      if (qualification === 'OBJECT') {
        console.log(`[GameLogicService] Skipping OBJECT (handled separately)`);
        continue;
      }

      // Déterminer la liste cible selon la qualification
      const targetList =
        qualification === 'FEATURE' ? currentGameState.data.skills : currentGameState.data.butterflyEffects;

      // Vérifier si l'attribut existe déjà
      const exists = targetList.some((attr) => attr.attributeId === attributeId);

      if (exists) {
        console.log(`[GameLogicService] Attribute ${attributeId} already exists, skipping`);
        continue;
      }

      // Créer un nouveau GameDataAttribute avec la description
      const gameDataAttribute = {
        attributeId,
        value: attributeValue,
        dropped: 0,
        description: stepAttr.description || '', // Sauvegarder la description
      };

      console.log(
        `[GameLogicService] Adding new attribute ${attributeId} to ${qualification === 'FEATURE' ? 'skills' : 'butterflyEffects'}`,
      );

      // Ajouter à la liste appropriée et mettre à jour currentGameState
      if (qualification === 'FEATURE') {
        const updatedSkills = [...currentGameState.data.skills, gameDataAttribute];
        currentGameState = {
          ...currentGameState,
          userId: currentGameState.userId, // Préserver userId
          data: {
            ...currentGameState.data,
            skills: updatedSkills,
          },
        };

        // Notification pour FEATURE
        notifications.push({
          title: 'Gain de compétence !',
          description: `Vous gagnez ${stepAttr.name}`,
          isCheat: false,
        });
        console.log(`[GameLogicService] Added FEATURE notification for ${stepAttr.name}`);
      } else {
        // BUTTERFLY_EFFECT
        const updatedButterflyEffects = [...currentGameState.data.butterflyEffects, gameDataAttribute];
        currentGameState = {
          ...currentGameState,
          userId: currentGameState.userId, // Préserver userId
          data: {
            ...currentGameState.data,
            butterflyEffects: updatedButterflyEffects,
          },
        };
      }
    }

    console.log(`[GameLogicService] Final skills count: ${currentGameState.data.skills.length}`);
    console.log(`[GameLogicService] Final skills:`, currentGameState.data.skills.map(s => s.attributeId));
    console.log(
      `[GameLogicService] Final butterflyEffects count: ${currentGameState.data.butterflyEffects.length}`,
    );
    console.log(`[GameLogicService] Final butterflyEffects:`, currentGameState.data.butterflyEffects.map(b => b.attributeId));

    return {notifications, updatedGameState: currentGameState};
  }

  /**
   * Traite les événements d'une étape
   * Retourne les notifications générées par les événements
   * Identique à Flutter PlayerController._processStepEvents
   */
  private async processStepEvents(gameState: GameState, step: PlayerStep): Promise<PlayerNotification[]> {
    const notifications: PlayerNotification[] = [];

    console.log(`[GameLogicService] ===== processStepEvents START =====`);
    console.log(`[GameLogicService] Step numero: ${step.numero}`);
    console.log(`[GameLogicService] Step events count: ${step.events?.length || 0}`);

    // Vérifier que step.events est un tableau avant de l'itérer
    if (step.events && Array.isArray(step.events)) {
      for (const event of step.events) {
        const eventType = event.type || event.typeName || '';
        console.log(`[GameLogicService] Processing event: ${eventType}`);

        // Gérer les événements ATTRIBUTE (modification d'attribut)
        if (eventType === 'ATTRIBUTE' || eventType === 'attribute') {
          const attributeId = event.attributeId || event.attribute;
          const value = event.value || '';
          if (attributeId) {
            console.log(`[GameLogicService] Processing ATTRIBUTE event: ${attributeId} = ${value}`);
            await playerService.updateAttribute(gameState, attributeId, value);
          }
        }
        // Gérer les événements AUTO_ADD_OBJECT (ajout automatique d'objet)
        else if (eventType === 'AUTO_ADD_OBJECT' || eventType === 'auto_add_object') {
          const objectId = event.attributeId || event.attribute || event.object_id || event.objectId;
          if (!objectId) {
            console.warn(`[GameLogicService] AUTO_ADD_OBJECT event missing object ID`);
            continue;
          }

          console.log(`[GameLogicService] Processing AUTO_ADD_OBJECT event: ${objectId}`);

          // Vérifier si l'objet n'est pas déjà dans l'inventaire
          const existingObject = gameState.data.objects?.find(
            (obj) => obj.attributeId === objectId || obj.attributeId?.toString() === objectId.toString(),
          );

          if (existingObject) {
            console.log(`[GameLogicService] Object ${objectId} already in inventory, skipping`);
            continue;
          }

          // Chercher l'objet dans step.objects ou step.attributes avec qualification OBJECT
          let objectToAdd = null;
          if (step.objects) {
            objectToAdd = step.objects.find((obj) => obj.id === objectId || obj.id?.toString() === objectId.toString());
          }
          if (!objectToAdd && step.attributes) {
            const attrObj = step.attributes.find(
              (attr) =>
                (attr.name === objectId || attr.name?.toString() === objectId.toString()) &&
                attr.qualification?.toUpperCase() === 'OBJECT',
            );
            if (attrObj) {
              // Créer un objet basique depuis l'attribut
              objectToAdd = {
                id: objectId,
                name: attrObj.name,
                value: attrObj.value || '',
              };
            }
          }

          if (objectToAdd) {
            // Créer un nouveau GameDataAttribute pour l'inventaire
            const newObject = {
              attributeId: objectId,
              value: objectToAdd.value || '',
              dropped: 0,
            };

            // Ajouter à l'inventaire
            const updatedObjects = [...(gameState.data.objects || []), newObject];
            gameState.data.objects = updatedObjects;

            const objectName = objectToAdd.name || objectId;
            notifications.push({
              title: 'Objet obtenu !',
              description: `Vous avez obtenu ${objectName}.`,
              isCheat: false,
            });

            console.log(`[GameLogicService] Added object ${objectId} (${objectName}) to inventory`);
          } else {
            console.warn(`[GameLogicService] Object ${objectId} not found in step.objects or step.attributes`);
          }
        }
      }
    }


    console.log(`[GameLogicService] processStepEvents completed: ${notifications.length} notifications`);
    return notifications;
  }

  /**
   * Migre les anciennes données notebook (string) vers le nouveau format (NotebookEntry[])
   * Appelé automatiquement lors du chargement d'un gameState
   * Fonction statique pour pouvoir être appelée depuis gameStateService
   */
  static migrateNotebookData(gameState: GameState): void {
    // Migrer notebookAuto depuis string vers array
    if (gameState.data.notebookAuto === undefined || gameState.data.notebookAuto === null) {
      gameState.data.notebookAuto = [];
    } else if (typeof gameState.data.notebookAuto === 'string') {
      const notebookString = gameState.data.notebookAuto;
      if (notebookString.trim()) {
        // Séparer par \n\n et créer des entrées
        const entries = notebookString.split('\n\n')
          .map(e => e.trim())
          .filter(e => e.length > 0)
          .map((content, index) => ({
            id: `migrated-auto-${Date.now()}-${index}`,
            content,
            source: 'auto' as const,
            createdAt: new Date().toISOString(),
          }));
        gameState.data.notebookAuto = entries;
        console.log(`[GameLogicService] Migrated notebookAuto from string to array: ${entries.length} entries`);
      } else {
        gameState.data.notebookAuto = [];
      }
    }

    // Migrer notebookManual depuis string vers array
    if (gameState.data.notebookManual === undefined || gameState.data.notebookManual === null) {
      gameState.data.notebookManual = [];
    } else if (typeof gameState.data.notebookManual === 'string') {
      const notebookString = gameState.data.notebookManual;
      if (notebookString.trim()) {
        // Séparer par \n\n et créer des entrées
        const entries = notebookString.split('\n\n')
          .map(e => e.trim())
          .filter(e => e.length > 0)
          .map((content, index) => ({
            id: `migrated-manual-${Date.now()}-${index}`,
            content,
            source: 'manual' as const,
            createdAt: new Date().toISOString(),
          }));
        gameState.data.notebookManual = entries;
        console.log(`[GameLogicService] Migrated notebookManual from string to array: ${entries.length} entries`);
      } else {
        gameState.data.notebookManual = [];
      }
    }

    // Si notebook existe mais pas notebookAuto, migrer aussi
    if (gameState.data.notebook && typeof gameState.data.notebook === 'string' && gameState.data.notebook.trim()) {
      if (gameState.data.notebookAuto.length === 0) {
        const notebookString = gameState.data.notebook;
        const entries = notebookString.split('\n\n')
          .map(e => e.trim())
          .filter(e => e.length > 0)
          .map((content, index) => ({
            id: `migrated-notebook-${Date.now()}-${index}`,
            content,
            source: 'auto' as const,
            createdAt: new Date().toISOString(),
          }));
        gameState.data.notebookAuto = entries;
        console.log(`[GameLogicService] Migrated notebook (deprecated) to notebookAuto: ${entries.length} entries`);
      }
    }
  }

  /**
   * Traite le notebook d'une étape et l'ajoute au notebookAuto si nécessaire
   * Retourne une notification si une nouvelle note a été ajoutée, null sinon
   * Indépendant des événements - le notebook génère sa propre notification
   */
  private async processStepNotebook(gameState: GameState, step: PlayerStep): Promise<PlayerNotification | null> {
    console.log(`[GameLogicService] ===== processStepNotebook START =====`);
    console.log(`[GameLogicService] Step numero: ${step.numero}`);
    console.log(`[GameLogicService] Step notebook value:`, step.notebook);
    console.log(`[GameLogicService] Step notebook type:`, typeof step.notebook);
    
    // Migrer les données si nécessaire (au cas où on charge un ancien gameState)
    const beforeMigration = JSON.stringify(gameState.data.notebookAuto);
    GameLogicService.migrateNotebookData(gameState);
    const afterMigration = JSON.stringify(gameState.data.notebookAuto);
    if (beforeMigration !== afterMigration) {
      console.log(`[GameLogicService] Migration occurred: ${beforeMigration} -> ${afterMigration}`);
    }

    // Mettre à jour le notebook automatique si présent (ajouter le contenu de l'étape au notebookAuto)
    // STRUCTURE: notebookAuto est maintenant un tableau de NotebookEntry
    // IMPORTANT: Ne l'ajouter qu'une seule fois par étape (vérification par stepNumber)
    if (step.notebook && typeof step.notebook === 'string' && step.notebook.trim()) {
      const currentNotebookAuto = gameState.data.notebookAuto || [];
      const newContent = step.notebook.trim();
      
      console.log(`[GameLogicService] Current notebookAuto type:`, Array.isArray(currentNotebookAuto) ? 'array' : typeof currentNotebookAuto);
      console.log(`[GameLogicService] Current notebookAuto entries count: ${Array.isArray(currentNotebookAuto) ? currentNotebookAuto.length : 'N/A'}`);
      
      if (Array.isArray(currentNotebookAuto)) {
        console.log(`[GameLogicService] Current notebookAuto entries:`);
        currentNotebookAuto.forEach((entry, index) => {
          console.log(`[GameLogicService]   Entry ${index + 1}:`, {
            id: entry.id,
            stepNumber: entry.stepNumber,
            source: entry.source,
            contentPreview: entry.content.substring(0, 50) + (entry.content.length > 50 ? '...' : ''),
          });
        });
      }
      
      console.log(`[GameLogicService] New entry content: "${newContent.substring(0, 100)}${newContent.length > 100 ? '...' : ''}"`);
      console.log(`[GameLogicService] New entry content length: ${newContent.length}`);
      
      // Vérifier si une entrée pour cette étape existe déjà (par stepNumber)
      const entryByStepNumber = currentNotebookAuto.find(
        entry => entry.source === 'auto' && entry.stepNumber === step.numero
      );
      const entryAlreadyPresent = !!entryByStepNumber;
      
      // Vérifier aussi si le contenu exact existe déjà (protection supplémentaire)
      const entryByContent = currentNotebookAuto.find(
        entry => entry.content.trim() === newContent
      );
      const contentAlreadyPresent = !!entryByContent;
      
      console.log(`[GameLogicService] Checking for duplicates:`);
      console.log(`[GameLogicService]   - Entry by stepNumber (${step.numero}):`, entryByStepNumber ? `FOUND (id: ${entryByStepNumber.id})` : 'NOT FOUND');
      console.log(`[GameLogicService]   - Entry by content:`, entryByContent ? `FOUND (id: ${entryByContent.id}, stepNumber: ${entryByContent.stepNumber})` : 'NOT FOUND');
      console.log(`[GameLogicService]   - entryAlreadyPresent: ${entryAlreadyPresent}`);
      console.log(`[GameLogicService]   - contentAlreadyPresent: ${contentAlreadyPresent}`);
      
      if (!entryAlreadyPresent && !contentAlreadyPresent) {
        // Créer une nouvelle entrée
        const newEntry = {
          id: `auto-${step.numero}-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
          content: newContent,
          source: 'auto' as const,
          stepNumber: step.numero,
          createdAt: new Date().toISOString(),
        };
        
        console.log(`[GameLogicService] Creating new entry:`, {
          id: newEntry.id,
          stepNumber: newEntry.stepNumber,
          source: newEntry.source,
          contentPreview: newEntry.content.substring(0, 50) + '...',
        });
        
        // Ajouter à la fin du tableau (ordre chronologique)
        const previousLength = currentNotebookAuto.length;
        gameState.data.notebookAuto = [...currentNotebookAuto, newEntry];
        const newLength = gameState.data.notebookAuto.length;
        
        console.log(`[GameLogicService] ✓ Added notebookAuto entry from step ${step.numero}`);
        console.log(`[GameLogicService] Array length: ${previousLength} -> ${newLength}`);
        console.log(`[GameLogicService] Updated notebookAuto entries:`);
        gameState.data.notebookAuto.forEach((entry, index) => {
          console.log(`[GameLogicService]   Entry ${index + 1}:`, {
            id: entry.id,
            stepNumber: entry.stepNumber,
            contentPreview: entry.content.substring(0, 50) + '...',
          });
        });
        console.log(`[GameLogicService] ===== processStepNotebook END (added) =====`);
        
        // Retourner une notification pour informer le joueur qu'une nouvelle note a été ajoutée
        return {
          title: 'Nouvelle note ajoutée',
          description: newContent.length > 50 ? `${newContent.substring(0, 50)}...` : newContent,
          isCheat: false,
        };
      } else {
        console.log(`[GameLogicService] ✗ Skipping notebookAuto entry from step ${step.numero} (entry already present)`);
        if (entryByStepNumber) {
          console.log(`[GameLogicService] Reason: Entry with stepNumber ${step.numero} already exists (id: ${entryByStepNumber.id})`);
        }
        if (entryByContent) {
          console.log(`[GameLogicService] Reason: Entry with same content already exists (id: ${entryByContent.id}, stepNumber: ${entryByContent.stepNumber})`);
        }
        console.log(`[GameLogicService] ===== processStepNotebook END (skipped) =====`);
        return null;
      }
    }
    
    console.log(`[GameLogicService] No notebook content in step ${step.numero}`);
    console.log(`[GameLogicService] ===== processStepNotebook END (no notebook) =====`);
    return null;
  }

  /**
   * Choisit un chemin (par index)
   */
  async choosePath(gameState: GameState, pathIndex: number, editorMode: boolean = false, currentStep?: PlayerStep): Promise<PlayerStep> {
    if (!this.currentGameState) {
      throw new Error('No game state');
    }

    // Charger la nouvelle étape via PlayerService
    // Passer editorMode pour ignorer les conditions si activé
    // Passer currentStep pour éviter de le recharger et garantir la cohérence
    const newStep = await playerService.choosePath(gameState, pathIndex, editorMode, currentStep);

    // Mettre à jour le gameState
    const updatedData = {
      ...gameState.data,
      currentStepNumber: newStep.numero,
    };

    const updatedGameState: GameState = {
      ...gameState,
      data: updatedData,
      dirty: true,
      lastUpdate: new Date().toISOString(),
      // S'assurer que userId est préservé
      userId: gameState.userId,
    };

    // Appeler getGameStep pour reconstruire l'étape complète
    // doEventsAndUpdateRecordedPaths = true car on vient d'un choix de path
    const gameStep = await this.getGameStep(updatedGameState, newStep, 'path', {
      doEventsAndUpdateRecordedPaths: true,
    });

    // IMPORTANT: getGameStep modifie updatedGameState par référence (ajoute compétences, objets, etc.)
    // et le sauvegarde déjà. Utiliser le gameState depuis gameStep pour être sûr d'avoir toutes les modifications.
    // Note: getGameStep a déjà sauvegardé via updateGameState à l'intérieur, donc pas besoin de re-sauvegarder
    const finalGameState = gameStep.gameState || updatedGameState;
    
    this.currentGameState = finalGameState;
    // Pas besoin de re-sauvegarder, getGameStep l'a déjà fait

    return gameStep;
  }

  /**
   * Exécute une action par son typeName
   * Reproduit la logique Java GameService.doAction()
   */
  async executeAction(
    gameState: GameState,
    actionTypeName: string,
    params: {[key: string]: any} = {},
  ): Promise<PlayerStep> {
    // Récupère l'étape actuelle
    const currentStep = await playerService.getCurrentStep(gameState);
    if (!currentStep) {
      throw new Error('No current step');
    }

    // Cherche l'ActionType correspondant dans le registre
    const actionTypeHandler = GameRegistry.getActionType(actionTypeName);
    if (!actionTypeHandler) {
      throw new Error(`No ActionType found for typeName: ${actionTypeName}`);
    }

    // Exécute l'ActionType (modifie le gameState directement)
    const notifications = await actionTypeHandler.execute(gameState, currentStep, params);

    // Recharger l'étape si nécessaire
    const updatedStep = await playerService.getCurrentStep(gameState);
    if (!updatedStep) {
      throw new Error('No current step after action');
    }

    // Appeler getGameStep() pour reconstruire l'étape complète
    // doEventsAndUpdateRecordedPaths = false car on vient d'une action, pas d'un choix de path
    const gameStep = await this.getGameStep(gameState, updatedStep, actionTypeName, {
      doEventsAndUpdateRecordedPaths: false,
    });

    // IMPORTANT: getGameStep modifie gameState (ajoute compétences, objets, etc.) et le sauvegarde
    // Utiliser le gameState attaché au gameStep qui contient les modifications
    const updatedGameState = gameStep.gameState || gameState;

    // Ajouter les notifications de l'action
    const finalStep: PlayerStep = {
      ...gameStep,
      notifications: [...gameStep.notifications, ...notifications],
    };

    // Mettre à jour currentGameState avec le gameState modifié qui contient les compétences
    this.currentGameState = updatedGameState;
    return finalStep;
  }

  getGameState(): GameState | null {
    return this.currentGameState;
  }

  setGameState(gameState: GameState): void {
    this.currentGameState = gameState;
  }

  setCurrentAdventure(adventure: PlayerAdventure): void {
    this.currentAdventure = adventure;
  }
}

export default new GameLogicService();
